import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  fetchAgentConfigs,
  updateAgentConfig,
  fetchModels,
  fetchPipelineConfigs,
  updatePipelineConfigs,
  fetchVisualConfig,
  updateVisualConfig,
  fetchBrandConfig,
  updateBrandConfig,
  getOrgApiKeys,
  setOrgApiKey,
  deleteOrgApiKey,
} from '../lib/api'
import type {
  AgentConfigListItem,
  AgentConfigUpdate,
  PipelineConfig,
  PipelineConfigsUpdatePayload,
  ModelRegistryItem,
  ModelInfo,
  BrandConfig,
  OrgApiKeyStatus,
} from '../lib/api'
import { getErrorMessage } from '../lib/errors'

const ADMIN_CONFIG_QUERY_KEY = 'admin-config'

export function useAgentConfigs(orgId: string | undefined) {
  return useQuery<AgentConfigListItem[]>({
    queryKey: [ADMIN_CONFIG_QUERY_KEY, 'agents', orgId],
    queryFn: () => fetchAgentConfigs(orgId!),
    enabled: Boolean(orgId),
  })
}

export function useUpdateAgentConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      agentId,
      orgId,
      data,
    }: {
      agentId: string
      orgId: string
      data: AgentConfigUpdate
    }) => updateAgentConfig(agentId, orgId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADMIN_CONFIG_QUERY_KEY, 'agents', variables.orgId],
      })
      toast.success('Configuracion del agente guardada')
    },
    onError: (err) => {
      toast.error('Error al guardar configuracion', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function useModels() {
  return useQuery<ModelRegistryItem[] | ModelInfo[]>({
    queryKey: [ADMIN_CONFIG_QUERY_KEY, 'models'],
    queryFn: fetchModels,
    staleTime: 1000 * 60 * 30,
  })
}

export function usePipelineConfigs(orgId: string | undefined) {
  return useQuery<PipelineConfig[]>({
    queryKey: [ADMIN_CONFIG_QUERY_KEY, 'pipelines', orgId],
    queryFn: () => fetchPipelineConfigs(orgId!),
    enabled: Boolean(orgId),
  })
}

export function useUpdatePipelineConfigs() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      orgId,
      data,
    }: {
      orgId: string
      data: PipelineConfigsUpdatePayload
    }) => updatePipelineConfigs(orgId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADMIN_CONFIG_QUERY_KEY, 'pipelines', variables.orgId],
      })
      toast.success('Configuracion de pipelines guardada')
    },
    onError: (err) => {
      toast.error('Error al guardar configuracion de pipelines', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function useVisualConfig(orgId: string | undefined) {
  return useQuery<Record<string, unknown>>({
    queryKey: [ADMIN_CONFIG_QUERY_KEY, 'visual', orgId],
    queryFn: () => fetchVisualConfig(orgId!),
    enabled: Boolean(orgId),
  })
}

export function useUpdateVisualConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      orgId,
      data,
    }: {
      orgId: string
      data: Record<string, unknown>
    }) => updateVisualConfig(orgId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADMIN_CONFIG_QUERY_KEY, 'visual', variables.orgId],
      })
      toast.success('Configuracion visual guardada')
    },
    onError: (err) => {
      toast.error('Error al guardar configuracion visual', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function useBrandConfig(orgId: string | undefined) {
  return useQuery<BrandConfig>({
    queryKey: [ADMIN_CONFIG_QUERY_KEY, 'brand', orgId],
    queryFn: () => fetchBrandConfig(orgId!),
    enabled: Boolean(orgId),
  })
}

export function useUpdateBrandConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      orgId,
      data,
    }: {
      orgId: string
      data: Partial<BrandConfig>
    }) => updateBrandConfig(orgId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADMIN_CONFIG_QUERY_KEY, 'brand', variables.orgId],
      })
      toast.success('Configuracion de marca guardada')
    },
    onError: (err) => {
      toast.error('Error al guardar configuracion de marca', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function useOrgApiKeys(organizationId: string | undefined) {
  return useQuery<OrgApiKeyStatus[]>({
    queryKey: [ADMIN_CONFIG_QUERY_KEY, 'api-keys', organizationId],
    queryFn: () => getOrgApiKeys(),
    enabled: Boolean(organizationId),
  })
}

export function useSetOrgApiKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ provider, key }: { provider: string; key: string }) =>
      setOrgApiKey(provider, key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ADMIN_CONFIG_QUERY_KEY, 'api-keys'] })
      toast.success('API key guardada')
    },
    onError: (err) => {
      toast.error('Error al guardar API key', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function useDeleteOrgApiKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ provider }: { provider: string }) => deleteOrgApiKey(provider),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ADMIN_CONFIG_QUERY_KEY, 'api-keys'] })
      toast.success('API key eliminada')
    },
    onError: (err) => {
      toast.error('Error al eliminar API key', {
        description: getErrorMessage(err),
      })
    },
  })
}
